import type { Activity, Catalog, Service } from '@/prisma/client'

export function slugify(name: string = ''): string {
  if (!name) return ''
  return name
    .toLowerCase()
    .normalize('NFD') // Normalizza per separare caratteri accentati (es. "à" -> "a`")
    .replace(/[\u0300-\u036f]/g, '') // Rimuove i caratteri diacritici (es. "`")
    .replace(/[^a-z0-9-]/g, '-') // Sostituisce tutti i caratteri non alfanumerici o trattini con trattini
    .replace(/-+/g, '-') // Sostituisce più trattini consecutivi con un singolo trattino
    .replace(/^-+|-+$/g, '') // Rimuove i trattini all'inizio e alla fine
}

export const useUtils = () => {
  const excerpt = (html: string, wordLimit: number = 10): string => {
    if (!html) return ''
    const text = html.replace(/<[^>]+>/g, ' ')
    const words = text.trim().split(/\s+/)
    const truncatedWords = words.slice(0, wordLimit)
    const truncatedText = truncatedWords.join(' ')
    return words.length > wordLimit ? truncatedText + '...' : truncatedText
  }
  const currentDomain = computed(() => {
    const domainPart = useRuntimeConfig().public.baseURL.replace(/^(https?:\/\/)/, '')
    const parts = domainPart.split('.')
    if (parts.length >= 2) {
      return parts.slice(-2).join('.')
    } else {
      // Handle cases where there are fewer than two parts (e.g., "localhost")
      return domainPart
    }
  })
  const getActivityUrl = (activity: Partial<Activity>) => {
    return `https://${activity.subdomain}.${currentDomain.value}`
  }
  const getServiceUrl = (service: Partial<Service>, catalog: Partial<Catalog>, activity: Partial<Activity>) => {
    return `https://${activity.subdomain}.${currentDomain.value}/${catalog.id}/${slugify(catalog.name)}/${service.id}/${slugify(service.name)}`
  }
  const getCatalogUrl = (catalog: Partial<Catalog>, activity: Partial<Activity>) => {
    return `https://${activity.subdomain}.${currentDomain.value}/${catalog.id}/${slugify(catalog.name)}`
  }
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (error) {
      try {
        unsecuredCopyToClipboard(text)
        return true
      } catch (error2) {
        console.error('Unable to copy to clipboard:', error2)
      }
    }
    return false
  }
  function unsecuredCopyToClipboard(text: string) {
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
      document.execCommand('copy')
    } catch (err) {
      throw new Error('Unable to copy to clipboard')
    }
    document.body.removeChild(textArea)
  }
  const windowWidth = ref(0)
  const isMobile = computed(() => windowWidth.value < 768)
  const updateWidth = () => {
    windowWidth.value = window.innerWidth
  }
  onMounted(() => {
    if (import.meta.client) {
      updateWidth()
      window.addEventListener('resize', updateWidth)
    }
  })
  onUnmounted(() => {
    if (import.meta.client) {
      window.removeEventListener('resize', updateWidth)
    }
  })
  return {
    slugify, // Now refers to the top-level exported slugify function
    excerpt,
    currentDomain,
    getActivityUrl,
    getServiceUrl,
    getCatalogUrl,
    isMobile,
    copyToClipboard,
  }
}